import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams, useTerritories, useToasts } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    CreateInvoiceRequest,
    DefaultPagedResponse,
    Entity,
    Invoice,
    InvoiceLine,
    InvoiceRequest,
    InvoicingDashboardData,
    IUseQueryPaged,
    MutationResult,
    PagedResponse,
    QueryHookParams,
    QueryResult,
    QueryType,
    UpdateLineRequest,
} from "../models";
import { Operation } from "rfc6902";
import { InvoiceFlagRequest } from "../models/invoices/InvoiceFlagRequest";

interface IUseInvoiceQuery extends Omit<IUseQueryPaged<Invoice>, "createMutation" | "updateMutation"> {
    dashboardQuery: QueryResult<InvoicingDashboardData>;
    flaggedQuery: QueryResult<Invoice[]>;
    unpaidQuery: QueryResult<Invoice[]>;
    latestInvoiceNumberQuery: QueryResult<string | undefined>;
    createMutation: MutationResult<Invoice, unknown, CreateInvoiceRequest>;
    updateMutation: MutationResult<Invoice, unknown, InvoiceRequest>;
    unlockMutation: MutationResult<Invoice, unknown, void>;
    flagMutation: MutationResult<Invoice, unknown, InvoiceFlagRequest>;
    exportToQboMutation: MutationResult<void, unknown, void>;
    importFromQboMutation: MutationResult<Invoice, unknown, string, void>;
    updateLineMutation: MutationResult<InvoiceLine, unknown, UpdateLineRequest>;
    sendToCustomerMutation: MutationResult<Invoice, unknown, FormData>;
}

interface IUseInvoiceQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "flagged" | "unpaid" | "dashboard" | "latest-invoice-number";
    searchParams?: Record<string, string>;
}

export const useInvoiceQuery = (params?: IUseInvoiceQueryParams): IUseInvoiceQuery => {
    const invoiceId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { errorToast } = useToasts();
    const { isAuthorized } = useAuth();
    const { get, post, put, patch, del } = useAxios();
    const { selectedTerritoryIds } = useTerritories();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Invoices);
    const url = Endpoints.Invoices;

    const queryParamsWithArgs = {
        ...queryParams,
        territoryIds: selectedTerritoryIds.join(","),
    };

    const queryType = useMemo(() => {
        if (params?.queryType === "flagged") return "flagged";
        if (params?.queryType === "unpaid") return "unpaid";
        if (params?.queryType === "dashboard") return "dashboard";
        if (params?.queryType === "latest-invoice-number") return "latest-invoice-number";
        return getQueryType(invoiceId, params?.queryType);
    }, [invoiceId, params?.queryType]);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Invoice>>(
                getRequestOptions(
                    url,
                    { ...config, params: { ...config?.params, ...params?.searchParams } },
                    queryParamsWithArgs,
                    sortParams,
                    pageParams
                )
            ),
        queryKey: queryKeys.invoices.pagedList({
            ...queryParamsWithArgs,
            ...pageParams,
            ...sortParams,
            ...params?.searchParams,
        }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Invoice>>(
                getRequestOptions(url, config, queryParamsWithArgs, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.invoices.list({ ...queryParamsWithArgs, ...sortParams }),
        placeholderData: [],
        enabled: false,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Invoice>({ url: `${url}/${invoiceId}`, config }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const dashboardQuery = useGenericQuery({
        queryFn: (config) =>
            get<InvoicingDashboardData>(getRequestOptions(`${url}/dashboard`, config, queryParamsWithArgs)),
        queryKey: queryKeys.dashboards.invoicing({ ...queryParamsWithArgs }),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "dashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const flaggedQuery = useGenericQuery({
        queryFn: (config) => get<Invoice[]>(getRequestOptions(`${url}/flagged`, config)),
        queryKey: queryKeys.invoices.flagged(),
        placeholderData: [],
        enabled: isAuthorized && queryType === "flagged",
    });

    const unpaidQuery = useGenericQuery({
        queryFn: (config) => get<Invoice[]>(getRequestOptions(`${url}/unpaid`, config)),
        queryKey: queryKeys.invoices.unpaid(),
        placeholderData: [],
        enabled: isAuthorized && queryType === "unpaid",
    });

    const latestInvoiceNumberQuery = useGenericQuery({
        queryFn: (config) =>
            get<{ latestNumber: string }>({ url: `${url}/latest-invoice-number`, config }).then(
                (res) => res.latestNumber ?? ""
            ),
        queryKey: queryKeys.invoices.latestInvoiceNumber(),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "latest-invoice-number",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: CreateInvoiceRequest) => post<CreateInvoiceRequest, Invoice>({ url, data }),
        queryKey: queryKeys.invoices.all(),
        keysToInvalidate: [queryKeys.invoices.lists(), queryKeys.invoices.pagedLists()],
        successToast: "Invoice created",
        onError: (error) => {
            const message = (error as any)?.response?.data?.error ?? "An error occurred while creating the invoice";
            errorToast(message);
        },
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: InvoiceRequest) => put<InvoiceRequest, Invoice>({ url, data }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        keysToInvalidate: [queryKeys.invoices.lists(), queryKeys.invoices.pagedLists()],
        disableSuccessToast: true,
    });

    const unlockMutation = useGenericMutation({
        mutationFn: () => put<void, Invoice>({ url: `${url}/${invoiceId}/unlock` }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        keysToInvalidate: [],
        successToast: "Invoice unlocked",
    });

    const flagMutation = useGenericMutation({
        mutationFn: (data: InvoiceFlagRequest) => put<InvoiceFlagRequest, Invoice>({ url: `${url}/flag`, data }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        keysToInvalidate: [],
        successToast: "Invoice flagged",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${invoiceId}` }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        keysToInvalidate: [queryKeys.invoices.lists(), queryKeys.invoices.pagedLists(), queryKeys.tissues.invoices()],
        successToast: "Invoice deleted",
    });

    const exportToQboMutation = useGenericMutation({
        mutationFn: () => post<void, void>({ url: `${url}/${invoiceId}/export` }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        successToast: "Invoice exported to QuickBooks",
        onError: (error) => {
            const message =
                (error as any)?.response?.data?.error ?? "An error occurred while exporting the invoice to QuickBooks";
            errorToast(message);
        },
    });

    const importFromQboMutation = useGenericMutation({
        mutationFn: (qboInvoiceId: string) => post<void, Invoice>({ url: `${url}/${qboInvoiceId}/import` }),
        queryKey: queryKeys.invoices.all(),
        successToast: "Invoice imported from QuickBooks",
        onError: (error) => {
            const message =
                (error as any)?.response?.data?.error ??
                "An error occurred while importing the invoice from QuickBooks";
            errorToast(message);
        },
    });

    const updateLineMutation = useGenericMutation({
        mutationFn: (request: UpdateLineRequest) =>
            patch<Operation[], InvoiceLine>({ url: `${url}/line/${request.lineId}`, data: request.data }),
        queryKey: queryKeys.invoices.lines({ invoiceId }),
        keysToInvalidate: [queryKeys.invoices.detail(invoiceId)],
    });

    const sendToCustomerMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, Invoice>({ url: `${url}/${invoiceId}/send-to-customer`, data }),
        queryKey: queryKeys.invoices.detail(invoiceId),
        successToast: "Invoice sent to customer",
    });

    return useMemo<IUseInvoiceQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            dashboardQuery,
            flaggedQuery,
            unpaidQuery,
            latestInvoiceNumberQuery,
            createMutation,
            updateMutation,
            unlockMutation,
            flagMutation,
            deleteMutation,
            exportToQboMutation,
            importFromQboMutation,
            updateLineMutation,
            sendToCustomerMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            detailQuery,
            dashboardQuery,
            flaggedQuery,
            unpaidQuery,
            latestInvoiceNumberQuery,
            createMutation,
            updateMutation,
            unlockMutation,
            flagMutation,
            deleteMutation,
            exportToQboMutation,
            importFromQboMutation,
            updateLineMutation,
            sendToCustomerMutation,
        ]
    );
};
